import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { usePermissions } from '../hooks/usePermissions';
import { CycleTimeSection } from '../components/reports/CycleTimeSection';
import { HoursReportSection } from '../components/reports/HoursReportSection';
import { OverdueSection } from '../components/reports/OverdueSection';
import { OverrunsSection } from '../components/reports/OverrunsSection';
import {
  DEFAULT_FILTER_STATE,
  ReportFilterBar,
  toReportFilters,
  type ReportFilterState,
  type ReportPeriod,
} from '../components/reports/ReportFilterBar';
import { ReportKpiCards } from '../components/reports/ReportKpiCards';
import { ThroughputSection } from '../components/reports/ThroughputSection';
import { WorkloadSection } from '../components/reports/WorkloadSection';

// Task reports page (/reports/tasks), gated by tasks.canViewReports. The
// filter state lives in the URL so reports are shareable/bookmarkable.

const PERIODS: ReportPeriod[] = ['thisMonth', 'lastMonth', 'last30', 'thisQuarter', 'custom'];

function stateFromParams(params: URLSearchParams): ReportFilterState {
  const period = params.get('period');
  return {
    period: PERIODS.includes(period as ReportPeriod)
      ? (period as ReportPeriod)
      : DEFAULT_FILTER_STATE.period,
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    projectId: params.get('projectId') ?? '',
    assigneeId: params.get('assigneeId') ?? '',
    departmentId: params.get('departmentId') ?? '',
    label: params.get('label') ?? '',
  };
}

function paramsFromState(state: ReportFilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.period !== DEFAULT_FILTER_STATE.period) params.set('period', state.period);
  if (state.period === 'custom') {
    if (state.from) params.set('from', state.from);
    if (state.to) params.set('to', state.to);
  }
  if (state.projectId) params.set('projectId', state.projectId);
  if (state.assigneeId) params.set('assigneeId', state.assigneeId);
  if (state.departmentId) params.set('departmentId', state.departmentId);
  if (state.label) params.set('label', state.label);
  return params;
}

export function TaskReportsPage() {
  const { t } = useI18n();
  const { can, isLoading: permissionsLoading } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => stateFromParams(searchParams), [searchParams]);
  const filters = useMemo(() => toReportFilters(state), [state]);

  if (permissionsLoading) {
    return <p className="p-6 text-sm text-gray-400">{t('reports.loading')}</p>;
  }
  if (!can('tasks', 'canViewReports')) {
    return <p className="p-6 text-sm text-gray-500">{t('reports.noAccess')}</p>;
  }

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 sm:p-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">{t('reports.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('reports.subtitle')}</p>
      </div>
      <ReportFilterBar
        value={state}
        onChange={(next) => setSearchParams(paramsFromState(next), { replace: true })}
      />
      <ReportKpiCards filters={filters} />
      <HoursReportSection filters={filters} />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ThroughputSection filters={filters} />
        <CycleTimeSection filters={filters} />
      </div>
      <OverrunsSection filters={filters} />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <WorkloadSection filters={filters} />
        <OverdueSection filters={filters} />
      </div>
    </div>
  );
}
